import { SortingState } from "@tanstack/react-table";
import { DynamicMenuItem } from "Common/Components/DynamicActionMenu/DynamicActionMenu";
import { useEffect, useMemo, useState } from "react";
import { Col, Row } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { createSelector } from "reselect";
import {
  exportGRNCSV,
  fetchGRNList,
} from "slices/Warehouse/ItemInward/GRN/thunk";
import { resetGRNState } from "slices/Warehouse/ItemInward/GRN/reducer";

import { Visibility } from "@mui/icons-material";
import EditIcon from "@mui/icons-material/Edit";
import UndoIcon from "@mui/icons-material/Undo";
import { showCustomSwal, showLoading, hideLoading } from "helpers/swalHelper";
import { revertGRN } from "slices/Warehouse/ItemInward/GRN/thunk";
import ExportButton from "Common/Components/Buttons/ExportButton";
import ColumnVisibilityDropdown from "Common/Components/ColumnFilteringPopUp/ColumnFilteringPopUp";
import FormattedDate from "Common/Components/NameWithDate.tsx/FormattedDate";
import SearchBox from "Common/Components/SearchBox/SearchBox";
import MasterTableContainer from "Common/Tables/MasterFiltersTable/MasterTableContainer";
import LoadingSpinner from "components/LoadingSpinner";
import { API_ENDPOINT } from "helpers/Endpoints/endpoints";
import { exportPendingPOCSV } from "slices/Warehouse/ItemInward/PendingPO/thunk";
import NameWithDate from "Common/Components/NameWithDate.tsx/NameWithDate";
import { app_modules } from "helpers/apps_modules_list";
import usePermission from "hooks/usePermission";
import { TableKeys } from "Constants/TableKeys/TableKeys";
import usePreservedTableState from "CustomHooks/usePreservedTableState";

type GRNProps = {
  warehouseId: string;
};

const WarehouseGRN: React.FC<GRNProps> = ({ warehouseId }) => {
  document.title = "Goods Receipt Notes";

  const tableState = usePreservedTableState({
    key: TableKeys.WAREHOUSE.ITEM_INWARD.GRN,
    defaults: {
      pageIndex: 0,
      pageSize: 10,
      search: "",
      searchInput: "",
      sorting: [],
      columnFilters: [],
      filterValue: [],
    },
  });

  const {
    pageIndex,
    pageSize,
    search,
    searchInput,
    sorting,
    columnFilters,
    filterValue,
    setPageIndex,
    setPageSize,
    setSearch,
    setSearchInput,
    setSorting,
    setColumnFilters,
    setFilterValue,
    resetState,
  } = tableState;

  // const [pageIndex, setPageIndex] = useState(0);
  // const [pageSize, setPageSize] = useState(10);
  // const [columnFilters, setColumnFilters] = useState<any>([]);
  // const [sorting, setSorting] = useState<SortingState>([]);
  // const [search, setSearch] = useState("");
  // const [searchInput, setSearchInput] = useState("");
  // const [filterValue, setFilterValue] = useState<any[]>([]);
  const [data, setData] = useState([]);
  const [tableInstance, setTableInstance] = useState<any>();
  const sortField = sorting[0]?.id ?? "";
  const sortDirection = sorting[0]?.desc ? "desc" : "asc";
  const navigate = useNavigate();
  const dispatch = useDispatch<any>();

  const Warehouse = createSelector(
    (state: any) => state.warehouseGRN,
    (item) => ({
      list: Array.isArray(item?.list) ? item.list : item?.list?.data || [],
      rowCount: item?.rowCount || 0,
      loading: item.loading,
      error: item.error,
    }),
  );

  const { list, rowCount, loading, error } = useSelector(Warehouse);

  const { canView, canCreate, canUpdate, canDelete } = usePermission(
    app_modules?.warehouse?.app_name,
    app_modules?.warehouse?.modules?.item_inward,
  );

  useEffect(() => {
    if (warehouseId) {
      dispatch(
        fetchGRNList({
          page: pageIndex + 1,
          pageSize,
          sort: sortField,
          order: sortDirection,
          search,
          filters: filterValue,
          warehouse_id: warehouseId,
        }),
      );
    } else {
      dispatch(resetGRNState());
    }
  }, [
    dispatch,
    pageIndex,
    pageSize,
    sorting,
    search,
    filterValue,
    warehouseId,
  ]);

  useEffect(() => {
    if (list && list.length > 0) {
      setData(list);
    } else {
      setData([]);
    }
  }, [list]);

  // Debouncing
  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      setSearch(searchInput);
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchInput]);

  const [revertingId, setRevertingId] = useState<string | null>(null);

  const handleRevert = (inventory_id: string) => {
    showCustomSwal({
      title: "Are you sure?",
      text: "You want to revert this GRN!",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Yes, revert it!",
      cancelButtonText: "Cancel",
    }).then((result: any) => {
      if (result.isConfirmed) {
        setRevertingId(inventory_id);
        showLoading("Reverting...");
        dispatch(revertGRN(inventory_id))
          .unwrap()
          .then((res: any) => {
            hideLoading();
            setRevertingId(null);
            showCustomSwal({
              title: "Reverted!",
              text: "GRN has been reverted.",
              icon: "success",
            });
            // refresh list
            dispatch(
              fetchGRNList({
                page: pageIndex + 1,
                pageSize,
                sort: sortField,
                order: sortDirection,
                search,
                filters: filterValue,
                warehouse_id: warehouseId,
              }),
            );
          })
          .catch((error: any) => {
            hideLoading();
            setRevertingId(null);
            console.error("Failed to revert GRN", error);
            showCustomSwal({
              title: "Error",
              text: error.message || error.error,
              icon: "error",
            });
          });
      }
    });
  };

  const columns = useMemo(() => {
    return [
      // {
      //   header: "Sr No.",
      //   accessorKey: "sr_no",
      //   enableColumnFilter: false,
      //   enableSorting: true,
      // },
      {
        header: "GRN NO.",
        accessorKey: "grn_no",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.ITEM_INWARD?.GRN?.FILTER_DROPDOWN(
            "grn_no",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.grn_no || "-",
      },
      {
        header: "PO No.",
        accessorKey: "purchase_order__po_no",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.ITEM_INWARD?.GRN?.FILTER_DROPDOWN(
            "purchase_order__po_no",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) =>
          row.original?.purchase_order?.po_no || "-",
      },
      {
        header: "Received Date",
        accessorKey: "received_date",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => (
          <FormattedDate date={row.original.received_date} />
        ),
      },

      {
        header: "Received By",
        accessorKey: "received_by",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "dropdown" as const,
        filterFetchUrl:
          API_ENDPOINT?.WAREHOUSE?.ITEM_INWARD?.GRN?.FILTER_DROPDOWN(
            "received_by",
            warehouseId,
          ),
        cell: ({ row }: { row: any }) => row.original.received_by || "-",
      },
      {
        header: "Created By/Date",
        accessorKey: "created_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.created_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.created_at}
            />
          );
        },
      },
      {
        header: "Updated By/Date",
        accessorKey: "updated_at",
        enableColumnFilter: true,
        enableSorting: true,
        filterType: "date" as const,
        cell: ({ row }: { row: any }) => {
          const user = row.original.updated_by_user;
          return (
            <NameWithDate
              name={user?.username}
              date={row.original.updated_at}
            />
          );
        },
      },
      {
        header: "Action",
        enableColumnFilter: false,
        enableSorting: false,
        cell: ({ row }: { row: any }) => {
          const data = row.original;
          const menuItems = [
            {
              icon: Visibility,
              label: "View",
              permission: canView,
              onClick: (item: any) => {
                navigate(`/grn-view-details/${data?.inventory_id}`, {
                  state: data,
                });
              },
              color: "primary",
            },
            {
              icon: EditIcon,
              label: "Edit",
              permission: canUpdate,
              onClick: (item: any) => {
                navigate(
                  `/warehouse-item-inward-edit-grn/${data?.purchase_order?.po_id}/${data?.inventory_id}`,
                  { state: data },
                );
              },
              color: "primary",
            },
            {
              icon: UndoIcon,
              label:
                revertingId === data?.inventory_id ? "Reverting..." : "Revert",
              permission: canUpdate,
              disabled: revertingId === data?.inventory_id,
              onClick: (item: any) => {
                if (revertingId !== data?.inventory_id)
                  handleRevert(data?.inventory_id);
              },
              color: "warning",
            },
          ].filter((item) => item.permission === true);
          return <DynamicMenuItem row={data} menuItems={menuItems} />;
        },
      },
    ];
  }, [dispatch, list, navigate, canView, warehouseId, revertingId]);

  return (
    <div
      className="d-flex flex-column"
      style={{
        flex: "1 1 auto",
        minHeight: 0,
        maxHeight: "100%",
        overflow: "hidden",
      }}
    >
      <div className="flex-shrink-0">
        <Row className="align-items-center mecord-search-row g-2">
          <SearchBox
            value={searchInput}
            onChange={(e: any) => setSearchInput(e.target.value)}
            placeholder="Search..."
          />

          <Col
            xs={12}
            md={12}
            lg={8}
            xl={7}
            className="d-flex justify-content-end align-items-center gap-2"
          >
            {tableInstance && (
              <ColumnVisibilityDropdown
                columns={tableInstance.getAllLeafColumns()}
                excludedColumns={["action"]}
              />
            )}
            {canView && (
              <ExportButton
                onExport={async () => {
                  return await exportGRNCSV({
                    warehouse_id: warehouseId,
                    search,
                    filters: filterValue,
                  });
                }}
              />
            )}
          </Col>
        </Row>
      </div>

      <div
        style={{
          flex: "1 1 auto",
          minHeight: 0,
          maxHeight: "100%",
          marginTop: 4,
          overflow: "hidden",
          display: "flex",
          flexDirection: "column",
        }}
      >
        {loading ? (
          <LoadingSpinner />
        ) : error ? (
          <div className="text-center py-4 text-danger">
            <h5>Error: {error}</h5>
          </div>
        ) : (
          <MasterTableContainer
            columns={columns}
            data={data || []}
            totalRecords={
              Number.isNaN(rowCount) || !rowCount ? 0 : Number(rowCount)
            }
            pageIndex={pageIndex}
            pageSize={pageSize}
            onPageChange={setPageIndex}
            onPageSizeChange={setPageSize}
            columnFilters={columnFilters}
            setColumnFilters={setColumnFilters}
            colValue={setFilterValue}
            sorting={sorting}
            onSortingChange={setSorting}
            isBordered={false}
            isPagination={true}
            manualSorting={true}
            onTableReady={setTableInstance}
          />
        )}
      </div>
    </div>
  );
};

export default WarehouseGRN;
